import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import BrandingPanel from '../components/BrandingPanel'
import BrandingDeploymentsPanel from '../components/BrandingDeploymentsPanel'
import BrandingWizard from '../components/BrandingWizard'
import {
  BrandingDeployment,
  BrandingTemplate,
  deleteBrandingTemplate,
  deployBranding,
  removeBrandingFromIntune,
  listBrandingDeployments,
  listBrandingTemplates,
  reassignBrandingDeployment,
  uninstallBrandingDeployment,
  rebuildBrandingTemplate,
  removeBrandingDeployment,
} from '../services/api'
import { Breadcrumb, ConfirmDialog, PageHeader, PageShell, Tabs } from '../components/ui'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { errorText } from '../utils/errorText'

/** What Refresh is refreshing, for the label a screen reader reads. */
const TAB_NOUN: Record<string, string> = {
  packages: 'branding packages',
  deployments: 'deployments',
}

/**
 * Settings -> Branding.
 *
 * NOT under Templates, and the distinction is the point. A template is attached to a deployment: you
 * pick one when you publish an application, and it describes that application's experience. Branding
 * is attached to a DEVICE — a signed config.psd1 at %ProgramData%\sap\psadt\config that every package
 * on that machine reads, whoever deployed it and whenever.
 *
 * TWO TABS, because they answer different questions. The packages are what you author and can still
 * change; the deployments are what devices actually received and cannot be changed at all — each
 * carries a frozen copy of what it shipped. Putting them in one list would suggest editing the first
 * alters the second, which is exactly what it does not do.
 *
 * THERE IS NO ESTATE-WIDE DEVICE TAB. Which machines hold a branding is a question about ONE
 * deployment — its own audience, and what each of those devices did with it — so it is answered on
 * that deployment's page, beside the assignment it belongs to. A tenant-wide table could only say
 * which branding won on each device, which is install order and nobody's to decide.
 */

type Tab = 'packages' | 'deployments'

const BrandingSettings: React.FC = () => {
  const qc = useQueryClient()
  // retry:false — a tenant with no branding is the normal starting state, not an error worth
  // retrying into.
  const templates = useQuery({
    queryKey: ['branding-templates'],
    queryFn: listBrandingTemplates,
    retry: false,
    // A BUILD FINISHES ON ITS OWN AND NOTHING TELLS THE PAGE. Signing is asynchronous, so a branding
    // saved a moment ago sits at Building with Deploy disabled until this list is read again — and
    // an author watching a row that never changes has no reason to think Refresh is what is needed.
    // Polled only while something is actually building, so a settled list costs nothing.
    refetchInterval: (q) =>
      (q.state.data?.templates ?? []).some((t) => t.state === 'building') ? 5000 : false,
  })
  const deployments = useQuery({
    queryKey: ['branding-deployments'],
    queryFn: listBrandingDeployments,
    retry: false,
  })

  const [tab, setTab] = useState<Tab>('packages')
  // `creating` and `editing` are separate: an edit is seeded from a row, a create is not, and one
  // nullable row would make "new" indistinguishable from "closed".
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<BrandingTemplate | null>(null)
  const [toDelete, setToDelete] = useState<BrandingTemplate | null>(null)
  const [toRemove, setToRemove] = useState<BrandingDeployment | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const wizardOpen = creating || !!editing

  /** The query behind the open tab, so Refresh refetches what is on screen rather than everything.
   *  The packages tab reads deployments too — it says which branding is live — so that one
   *  refetches both. */
  const tabQuery = tab === 'packages' ? templates : deployments
  const refreshTab = () => {
    void tabQuery.refetch()
    if (tab === 'packages') void deployments.refetch()
  }

  const remove = useMutation({
    mutationFn: (t: BrandingTemplate) => deleteBrandingTemplate(t.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding-templates'] })
      setRowError(null)
    },
    onError: (e: any) =>
      // Shown verbatim. A refusal names the deployments this branding has already shipped as, and
      // those are what is on devices — the whole reason the delete was refused.
      setRowError(errorText(e, 'Could not delete the branding.')),
  })

  // DEPLOY IS A SINGLE ACTION, with no screen: the audience was authored on the editor's last step
  // and is stored on the branding, so by the time this is pressed there is nothing left to ask. The
  // server reads that audience when the request names none.
  const [busyTemplate, setBusyTemplate] = useState<string | null>(null)
  // ALREADY DEPLOYED is not an error the reader can act on by itself. The server refuses a second
  // copy because one branding is one deployment — but republishing the SAME one is a real need
  // whenever the package has changed while the branding has not, and without a way past it the only
  // route is to undeploy by hand and remember to deploy again. So the 409 becomes a prompt.
  const [toRedeploy, setToRedeploy] = useState<BrandingTemplate | null>(null)
  const deploy = useMutation({
    mutationFn: ({ t, force }: { t: BrandingTemplate; force?: boolean }) =>
      deployBranding({ template_id: t.id, ...(force ? { force: true } : {}) }),
    onMutate: ({ t }) => setBusyTemplate(t.id),
    onSettled: () => setBusyTemplate(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding-deployments'] })
      setToRedeploy(null)
      setRowError(null)
    },
    onError: (e: any, { t, force }) => {
      // Only the un-forced attempt turns into a prompt. A 409 from the forced one is a different
      // refusal — Intune not connected, say — and showing the same dialog again would loop.
      if (!force && e?.response?.status === 409) {
        setToRedeploy(t)
        return
      }
      setToRedeploy(null)
      setRowError(errorText(e, 'Could not deploy this branding.'))
    },
  })

  // Building again changes nothing about the branding, so there is no confirmation: the action is
  // its own description, and a dialog asking "are you sure" about a no-op reads as a warning that
  // something is at stake. The row returns to Building, which is the feedback.
  // Uninstall from the audience AND delete the application from Intune, in that order on the
  // server. Confirmed first: for a device that has not checked in by then it is one-way.
  const [toRemoveFromIntune, setToRemoveFromIntune] =
    useState<{ t: BrandingTemplate; d: BrandingDeployment } | null>(null)
  const removeFromIntune = useMutation({
    mutationFn: (x: { t: BrandingTemplate; d: BrandingDeployment }) =>
      removeBrandingFromIntune(x.d.id),
    onMutate: (x) => setBusyTemplate(x.t.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding-deployments'] })
      setRowError(null)
    },
    onError: (e: any) => setRowError(errorText(e, 'Could not remove this branding from Intune.')),
    onSettled: () => {
      setBusyTemplate(null)
      setToRemoveFromIntune(null)
    },
  })

  const rebuild = useMutation({
    mutationFn: (t: BrandingTemplate) => rebuildBrandingTemplate(t.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding-templates'] })
      setRowError(null)
    },
    onError: (e: any) =>
      setRowError(errorText(e, 'Could not start the build.')),
  })

  // Uninstall is the only action that changes a device, so it confirms first — and the dialog says
  // what the devices will do rather than what the record will say.
  const [toUninstall, setToUninstall] = useState<BrandingDeployment | null>(null)
  const [busyDeployment, setBusyDeployment] = useState<string | null>(null)

  const uninstall = useMutation({
    mutationFn: (d: BrandingDeployment) => uninstallBrandingDeployment(d.id),
    onMutate: (d: BrandingDeployment) => setBusyDeployment(d.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding-deployments'] })
      setRowError(null)
    },
    onError: (e: any) =>
      setRowError(errorText(e, 'Could not start the uninstall.')),
    onSettled: () => {
      setBusyDeployment(null)
      setToUninstall(null)
    },
  })

  const removeDeployment = useMutation({
    mutationFn: (d: BrandingDeployment) => removeBrandingDeployment(d.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branding-deployments'] })
      setRowError(null)
    },
    onError: (e: any) =>
      setRowError(errorText(e, 'Could not remove the deployment.')),
  })

  const closeWizard = () => {
    setCreating(false)
    setEditing(null)
  }

  return (
    <PageShell size={wizardOpen ? 'full' : 'wide'}>
      <PageHeader
        title="Branding"
        subtitle="The logo, company name and dialog style a device shows — for every application on it, not one deployment."
      />

      {/* The tabs switch between the two LISTS, so they have nothing to do while the wizard is open.
          The whole wrapper goes rather than just the Tabs, or its margin leaves a gap where they
          were. The breadcrumb is the way back. */}
      {!wizardOpen ? (
        /* REFRESH SITS WITH THE TABS, and refetches whichever one is open. Every list here reports
           state that moves on its own — a branding finishes signing, a deployment reaches Intune, a
           device checks in — and none of it arrives by itself, so without this the only way to see
           the current answer is to leave the page and come back. */
        <div className="mb-4 flex items-end justify-between gap-3">
          <Tabs
            tabs={[
              { key: 'packages', label: 'Branding packages' },
              { key: 'deployments', label: 'Deployments' },
            ]}
            active={tab}
            onChange={(k) => setTab(k as Tab)}
          />
          <button
            type="button"
            onClick={() => refreshTab()}
            disabled={tabQuery.isFetching}
            aria-label={`Refresh ${TAB_NOUN[tab]}`}
            title={`Refresh ${TAB_NOUN[tab]}`}
            className="mb-2 flex shrink-0 items-center gap-1.5 rounded-lg border border-theme-border-primary px-2.5 py-1.5 text-[13px] text-theme-text-secondary hover:bg-theme-tertiary hover:text-theme-text-primary disabled:opacity-50"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${tabQuery.isFetching ? 'animate-spin' : ''}`}
            />
            {tabQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      ) : (
        <Breadcrumb
          trail={[
            { label: 'Branding', onClick: closeWizard },
            { label: editing ? `Edit ${editing.name}` : 'New branding' },
          ]}
        />
      )}

      {!wizardOpen && tab === 'packages' && (
        <BrandingPanel
          templates={templates.data?.templates ?? []}
          deployments={deployments.data?.deployments ?? []}
          loading={templates.isLoading}
          error={templates.isError ? 'Branding could not be loaded.' : null}
          onCreate={() => {
            setRowError(null)
            setCreating(true)
          }}
          onEdit={(t) => {
            setRowError(null)
            setEditing(t)
          }}
          onDelete={(t) => {
            setRowError(null)
            setToDelete(t)
          }}
          onDeploy={(t) => {
            setRowError(null)
            deploy.mutate({ t })
          }}
          busyId={busyTemplate}
          onUndeploy={(t, d) => {
            setRowError(null)
            setToRemoveFromIntune({ t, d })
          }}
          onRebuild={(t) => {
            setRowError(null)
            rebuild.mutate(t)
          }}
        />
      )}

      {!wizardOpen && tab === 'deployments' && (
        <BrandingDeploymentsPanel
          deployments={deployments.data?.deployments ?? []}
          templates={templates.data?.templates ?? []}
          loading={deployments.isLoading}
          error={deployments.isError ? 'Deployments could not be loaded.' : null}
          onRemove={(d) => {
            setRowError(null)
            setToRemove(d)
          }}
          onUninstall={(d) => {
            setRowError(null)
            setToUninstall(d)
          }}
          onUndeploy={(d) => {
            setRowError(null)
            const t = templates.data?.templates?.find((x) => x.identity === d.identity)
            setToRemoveFromIntune({ t: t ?? ({ ...d } as any), d })
          }}
          busyId={busyDeployment}
        />
      )}

      {wizardOpen && (
        <BrandingWizard
          template={editing ?? undefined}
          onClose={closeWizard}
          onSaved={() => qc.invalidateQueries({ queryKey: ['branding-templates'] })}
        />
      )}

      {rowError && (
        <p className="mt-3 rounded-lg border border-red-500/30 bg-red-500/[0.07] px-3 py-2 text-xs text-red-400">
          {rowError}
        </p>
      )}

      {/* Names what deleting does and does not do: devices holding this branding keep it, because a
          deployment carries its own frozen copy of what it shipped. */}
      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && remove.mutate(toDelete)}
        title={`Delete ${toDelete?.name ?? 'branding'}?`}
        confirmLabel="Delete branding"
        message={
          <>
            <p>
              Devices already holding this branding keep it — deleting here removes the branding you
              author, not the configuration on a machine.
            </p>
            <p className="mt-2">
              A branding that has been deployed cannot be deleted until its deployments are removed.
              This cannot be undone.
            </p>
          </>
        }
      />

      {/* REDEPLOY, offered when Deploy is refused because this branding already has a deployment.
          Not danger-styled: the devices keep their branding throughout, because the new package's
          install replaces the old one in place. What is destroyed is the Intune APPLICATION, and
          the dialog says so rather than leaving the reader to guess what "replace" reaches. */}
      <ConfirmDialog
        open={!!toRedeploy}
        onClose={() => setToRedeploy(null)}
        onConfirm={() => toRedeploy && deploy.mutate({ t: toRedeploy, force: true })}
        title={`Redeploy ${toRedeploy?.name ?? 'branding'}?`}
        confirmLabel="Replace the deployment"
        message={
          <>
            <p>
              This branding is already deployed. Redeploying publishes it again as a new application
              and deletes the old one from Intune, keeping the same audience.
            </p>
            <p className="mt-2">
              Devices stay branded throughout — the new package replaces the configuration in place
              rather than removing it first. Use this after a change to how brandings are packaged;
              to change only who receives this one, edit its assignment instead.
            </p>
          </>
        }
      />

      {/* Uninstall CHANGES DEVICES, which nothing else on this page does, so it confirms — and it
          describes what the machines will do rather than what the record will say. */}
      {/* THE ONE-WAY PART IS THE POINT OF THIS DIALOG. Uninstall keeps the application so a device
          that checks in late still gets the removal; this deletes it, so that device never can. */}
      <ConfirmDialog
        open={!!toRemoveFromIntune}
        onClose={() => setToRemoveFromIntune(null)}
        onConfirm={() => toRemoveFromIntune && removeFromIntune.mutate(toRemoveFromIntune)}
        title={`Undeploy ${toRemoveFromIntune?.t.name ?? 'branding'}?`}
        confirmLabel="Uninstall and undeploy"
        danger
        message={
          <>
            <p>
              This assigns the branding to uninstall, then deletes its application from Intune. Any
              device that checks in before the deletion removes the branding and goes back to the
              packaged logo, company name and wording.
            </p>
            <p className="mt-2">
              A device that has NOT checked in by then keeps this branding permanently — the
              application is the only thing that could remove it, and it will be gone. To be sure
              every machine is covered, use <strong>Uninstall</strong> on the Deployments tab
              instead: it withdraws the branding and keeps the application until the stragglers
              report in.
            </p>
          </>
        }
      />

      <ConfirmDialog
        open={!!toUninstall}
        onClose={() => setToUninstall(null)}
        onConfirm={() => toUninstall && uninstall.mutate(toUninstall)}
        title={`Uninstall ${toUninstall?.name ?? 'branding'} from its devices?`}
        confirmLabel="Uninstall from devices"
        message={
          <>
            <p>
              Every device holding this branding removes it on its next check-in and goes back to
              the packaged logo, company name and wording.
            </p>
            <p className="mt-2">
              Its Intune application is kept, because that is what performs the removal. Once the
              devices report it gone, the record can be removed too.
            </p>
          </>
        }
      />

      <ConfirmDialog
        open={!!toRemove}
        onClose={() => setToRemove(null)}
        onConfirm={() => toRemove && removeDeployment.mutate(toRemove)}
        title={`Remove ${toRemove?.name ?? 'deployment'}?`}
        confirmLabel="Remove deployment"
        message={
          <>
            {/* THE OLD WORDING WAS FALSE IN BOTH CLAUSES: it said the Intune application was
                removed and that no further device would receive it. Neither happens. Saying so
                plainly matters more here than anywhere else in this page, because the operator is
                about to do the one thing that cannot be undone from the portal. */}
            <p>
              This forgets our record. It does <strong>not</strong> remove the branding from any
              device, and it does not remove the application from Intune.
            </p>
            <p className="mt-2">
              The devices that have this branding keep it, and devices joining those groups will
              still receive it. Once the record is gone, nothing here can take it off them —
              removing a branding is something only its Intune application can do.
            </p>
            <p className="mt-2 text-theme-text-secondary">
              To actually un-brand those machines, use <strong>Uninstall</strong> first and remove
              the record afterwards.
            </p>
          </>
        }
      />
    </PageShell>
  )
}

export default BrandingSettings
